import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FormControl, Input, InputAdornment, InputLabel, MenuItem, Select } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export const AddPaymentModal = ({
    isOpen,
    onClose,
    operation,
    onConfirm,
}) => {
    const { t } = useTranslation();

    const [paymentData, setPaymentData] = useState({
        paymentType: 'deposit',
        paymentMethod: '',
        amountPaid: 0,
        referenceNumber: '',
        reqStatus: '',
    });
    const [errors, setErrors] = useState({});

    // Set amount paid to remaining balance when modal opens or operation changes
    useEffect(() => {
        if (isOpen && operation) {
            setPaymentData(prev => ({
                ...prev,
                amountPaid: operation.remainingBalance || 0
            }));
        }
    }, [isOpen, operation]);

    if (!isOpen || !operation) return null;

    const validateForm = () => {
        const newErrors = {};

        if (!paymentData.paymentMethod) {
            newErrors.paymentMethod = t('paymentMethodRequired') || 'Payment method is required';
        }

        if (!paymentData.amountPaid || paymentData.amountPaid <= 0) {
            newErrors.amountPaid = t('amountRequired') || 'Amount must be greater than 0';
        }

        if (paymentData.amountPaid > operation.remainingBalance) {
            newErrors.amountPaid = t('amountExceedsBalance') || 'Amount exceeds remaining balance';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = () => {
        if (!validateForm()) return;
        const reqStatus = handleReqStatus(paymentData);
        onConfirm(operation.id, { ...paymentData, reqStatus });
        handleClose();
    };

    const handleClose = () => {
        setPaymentData({
            paymentType: 'deposit',
            paymentMethod: '',
            amountPaid: operation?.remainingBalance || 0,
            referenceNumber: '',
            reqStatus: '',
        });
        setErrors({});
        onClose();
    };

    const handleInputChange = (field, value) => {
        setPaymentData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const paymentMethods = [
        { value: 'cash', label: t('paymentMethodCash') },
        { value: 'bankTransfer', label: t('paymentMethodBankTransfer') },
        { value: 'wallet', label: t('paymentMethodWallet') },
        { value: 'egyptianPost', label: t('paymentMethodEgyptianPost') }
    ];

    function getAmountPaidTotal(payments, newPaymentAmount) {
        let totalDeposits = newPaymentAmount;
        let totalRefunds = 0;
        payments?.forEach((ele) => {
            if (ele.paymentType === "deposit") {
                totalDeposits += parseFloat(ele.amountPaid) || 0;
            } else if (ele.paymentType === "refund") {
                totalRefunds += parseFloat(ele.amountPaid) || 0;
            }
        });
        return { totalDeposits, totalRefunds, netTotal: totalDeposits - totalRefunds };
    }

    function handleReqStatus(paymentData) {
        console.log("paymentData: ", paymentData);
        console.log("operation: ", operation);

        if (operation?.remainingBalance > paymentData?.amountPaid && paymentData?.amountPaid != 0) {
            return "deposit";
        } else if (operation?.remainingBalance <= paymentData?.amountPaid != 0) {
            return "settled";
        } else {
            return operation.reqStatus;
        }
    }

    const sendWhatsappMessage = () => {
        const totalAmountPaid = getAmountPaidTotal(operation.rawRequest.reqPayments, paymentData.amountPaid);

        if (!operation) return "";

        const message = `
                مرحبــا, ${operation.reqClientName}
    
                هـذه رسالة تـأكـيـد تـحـصــيـل دفعة
                لمسلسل حجز رقم : ${operation.reqCode}
                لوحدة رقم ${operation.unitCode} من يــوم ${moment(operation.reqFrom).format("DD/MM/YYYY")}

                القيمة المحصلة ${totalAmountPaid.netTotal}ج.م. القيمة المتبقية${operation.rawRequest.reqTotalCost - totalAmountPaid.netTotal}ج.م.
            `;
        return encodeURIComponent(message);
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-green-50 border-b border-slate-200 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <DollarSign size={20} className="text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-green-900">
                                    {t('addPayment') || 'Add Payment'}
                                </h2>
                                <p className="text-sm text-green-700">{operation.reqClientName} - {operation.unitName} - {operation.unitCode}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 text-green-400 hover:text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Booking Details */}
                    <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-medium text-slate-900 mb-3">
                            {t('bookingDetails')}:
                        </h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-slate-600">{t('guest')}:</span>
                                <span className="font-medium text-slate-900">{operation.reqClientName}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600">{t('unit')}:</span>
                                <span className="font-medium text-slate-900">{operation.unitName} - {operation.unitCode}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600">{t('reqCode')}:</span>
                                <span className="font-medium text-slate-900">{operation.reqCode}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-600">{t('operationRemainingBalance')}:</span>
                                <span className="font-bold text-orange-600">
                                    {operation.remainingBalance?.toLocaleString()} {t('EGP')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Fields - Material UI Style like RequestPaymentsFields */}
                    <div className="row align-items-start">
                        {/* Payment Method */}
                        <div className="col-12">
                            <div className='py-2 px-2'>
                                <FormControl className='textfield-input' variant="standard" fullWidth>
                                    <InputLabel id="payment-method-label">{t("paymentMethod")}</InputLabel>
                                    <Select
                                        MenuProps={{ disableScrollLock: true }}
                                        labelId="payment-method-label"
                                        id="paymentMethod"
                                        value={paymentData.paymentMethod}
                                        onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                                    >
                                        <MenuItem value={"bankTransfer"}>{t("bankTransfer")}</MenuItem>
                                        <MenuItem value={"egyptianPost"}>{t("egyptianPost")}</MenuItem>
                                        <MenuItem value={"wallet"}>{t("wallet")}</MenuItem>
                                        <MenuItem value={"cash"}>{t("cash")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            {errors.paymentMethod && (
                                <div className="text-helper-invalid px-2">{errors.paymentMethod}</div>
                            )}
                        </div>

                        {/* Amount Paid */}
                        <div className="col-md-6 col-12">
                            <div className='py-2 px-2'>
                                <FormControl fullWidth variant="standard">
                                    <InputLabel htmlFor="amount-paid-input">{t("amountPaid")}</InputLabel>
                                    <Input
                                        type='number'
                                        id="amountPaid"
                                        inputProps={{
                                            min: 0,
                                            max: operation.remainingBalance,
                                            step: "0.01"
                                        }}
                                        onChange={(e) => handleInputChange('amountPaid', parseFloat(e.target.value) || 0)}
                                        value={paymentData.amountPaid}
                                        endAdornment={<InputAdornment position="end">{t("EGP")}</InputAdornment>}
                                    />
                                </FormControl>
                            </div>
                            {errors.amountPaid && (
                                <div className="text-helper-invalid px-2">{errors.amountPaid}</div>
                            )}
                            <button
                                type="button"
                                onClick={() => handleInputChange('amountPaid', operation.remainingBalance)}
                                className="text-sm text-myPrimary hover:text-green-700 font-medium px-2"
                            >
                                {t('payFullAmount') || 'دفع المبلغ كاملاً'}
                            </button>
                        </div>

                        {/* Reference Number */}
                        <div className="col-md-6 col-12 d-flex align-items-end">
                            <div className='py-2 px-2 flex-grow-1'>
                                <FormControl fullWidth variant="standard">
                                    <InputLabel htmlFor="reference-number-input">{t("referenceNumber")}</InputLabel>
                                    <Input
                                        type='text'
                                        id="referenceNumber"
                                        onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                                        value={paymentData.referenceNumber}
                                    />
                                </FormControl>
                            </div>
                            {operation && operation.phone && (
                                <div className='pb-2 px-2'>
                                    <a target='_blank' rel="noreferrer" href={`https://wa.me/${operation.phone}?text=${sendWhatsappMessage(operation)}`}>
                                        <FontAwesomeIcon className='cursor-pointer fs-4 text-success' icon={faWhatsapp} />
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 rounded-b-2xl">
                    <div className="flex space-x-3 rtl:space-x-reverse">
                        <button
                            onClick={handleConfirm}
                            disabled={!paymentData.paymentMethod || !paymentData.amountPaid}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse"
                        >
                            <CreditCard size={18} />
                            <span>{t('confirmPayment') || 'Confirm Payment'}</span>
                        </button>
                        <button
                            onClick={handleClose}
                            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse"
                        >
                            <X size={18} />
                            <span>{t('Cancel')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
